import React from 'react';

import { Col, Row } from 'reactstrap';
import { styled } from 'styled-components';

import CarouselCard from 'components/Carousel/CarouselCard';
import CarouselSlider from 'components/Carousel/CarouselSlider';
import { CarouselSliderContainer } from 'components/Carousel/CarouselSlider';
import ContentContainer from 'components/ContentContainer/ContentContainer';
import CtaButton from 'components/CtaButton/CtaButton';
import FinalCard from 'components/ModularBlocks/ArticleFeedBlock/components/FinalCard';
import ContentCard from 'components/ModularBlocks/ContentCards/components/ContentCard';

import { ContentCardsI } from 'interfaces/content-stack/modular-blocks/contentCard';

import { getModularBlockLinkData } from 'utils/modularBlockUtils';

const StyledCol = styled(Col)`
  margin-bottom: 1.5rem;
`;

const StyledRow = styled(Row)`
  justify-content: center;
`;

const ContentCardsTitle = styled.div`
  font-weight: 500;
  line-height: 1.6;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
  font-size: 3rem;
  margin-bottom: 2rem;
  text-transform: uppercase;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    font-size: 2.75rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const ButtonsContainer = styled.div<{ $marginTop: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1rem;
  margin-top: ${(props) => (props.$marginTop ? '1.5rem' : '0')};

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    flex-direction: column;
    grid-row-gap: 1.5rem;
  }
`;

const ContentCardCarouselContainer = styled.div`
  ${CarouselSliderContainer} {
    .splide__track {
      height: 25.6rem;

      @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
        height: 26rem;
      }
    }
  }
`;

export interface ContentCardsProps {
  data: ContentCardsI;
  className: string;
}

const ContentCards = ({ data, className }: ContentCardsProps) => {
  const { contentCards, title, buttonCta, displayAsFeed, finalCardCta } = data;
  const lgColSize = contentCards.length > 4 ? 4 : 12 / contentCards.length;
  const mdColSize = contentCards.length > 4 ? 4 : contentCards.length % 2 === 0 ? 6 : lgColSize;

  return (
    <ContentContainer outerOverflow={displayAsFeed ? 'hidden' : 'visible'} className={className}>
      {title && <ContentCardsTitle>{title}</ContentCardsTitle>}
      {displayAsFeed ? (
        <ContentCardCarouselContainer>
          <CarouselSlider pagination>
            {contentCards.map((contentCard, i) => (
              <CarouselCard key={i}>
                <ContentCard carousel data={contentCard} />
              </CarouselCard>
            ))}
            {finalCardCta?.enabled && <FinalCard finalCard={finalCardCta} />}
          </CarouselSlider>
        </ContentCardCarouselContainer>
      ) : (
        <StyledRow>
          {contentCards.map((contentCard, i) => (
            <StyledCol md={mdColSize} lg={lgColSize} key={i}>
              <ContentCard data={contentCard} />
            </StyledCol>
          ))}
        </StyledRow>
      )}
      {buttonCta && (
        <ButtonsContainer $marginTop={displayAsFeed}>
          {buttonCta.map((button, index) => {
            const { link, openInNewWindow } = getModularBlockLinkData(button.buttonLink);

            return (
              <CtaButton
                key={index}
                to={link}
                buttonType={button.buttonType}
                text={button.buttonText}
                openInNewWindow={openInNewWindow}
                dataLayerInfo={title}
              />
            );
          })}
        </ButtonsContainer>
      )}
    </ContentContainer>
  );
};

export default ContentCards;
